import {
  initializeFaro,
  getWebInstrumentations,
  LogLevel,
  type EventAttributes,
  type Faro,
  type MetaAttributes,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

let faroInstance: Faro | null = null;

export function initFaro(): Faro | null {
  const config = globalThis.brightlyFaro;

  if (config?.enabled !== 'true') {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('[Faro] Disabled via configuration');
    }
    return null;
  }

  if (!config?.collectorUrl || !URL.canParse(config.collectorUrl)) {
    console.warn('[Faro] No collector URL configured, skipping initialization');
    return null;
  }

  if (faroInstance) {
    return faroInstance;
  }

  try {
    faroInstance = initializeFaro({
      url: config.collectorUrl,
      app: {
        name: config.appName || 'backstage',
        version: window.brightlyVersion || '0.0.0',
        environment: config.environment || 'development',
      },
      view: {
        name: location?.pathname || '/',
      },
      instrumentations: [
        ...getWebInstrumentations({ captureConsole: true }),
        new TracingInstrumentation(),
      ],
      consoleInstrumentation: {
        disabledLevels: [LogLevel.DEBUG],
      },
      sessionTracking: {
        enabled: true,
        persistent: true,
        samplingRate: config.samplingRate !== undefined ? Number(config.samplingRate) : 1,
      },
      batching: {
        enabled: true,
        sendTimeout: 1000,
        itemLimit: 50,
      },
    });

    return faroInstance;
  } catch (error) {
    console.error('[Faro] Failed to initialize:', error);
    return null;
  }
}

export function getFaro(): Faro | null {
  return faroInstance;
}

export function logEvent(name: string, attributes: EventAttributes = {}): void {
  faroInstance?.api.pushEvent(name, attributes);
}

export function logError(error: Error | string, context: MetaAttributes = {}): void {
  const errorToReport = error instanceof Error ? error : new Error(error);
  faroInstance?.api.pushError(errorToReport, { context });
}

export function setUser(user: { id?: string; attributes?: MetaAttributes }): void {
  faroInstance?.api.setUser({ id: user.id, attributes: user.attributes });
}

export function clearUser(): void {
  faroInstance?.api.resetUser();
}

export function setFaroView(path: string): void {
  faroInstance?.api.setView({ name: path });
}
